import { useState, useEffect, useRef } from 'react';

const API = `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem('token');

const DOC_TYPES = [
  { value: 'mop',              label: 'MOP Document' },
  { value: 'routing_diagram',  label: 'Routing Diagram' },
  { value: 'splicing_diagram', label: 'Splicing Diagram' },
  { value: 'as_built',         label: 'As-Built' },
  { value: 'other',            label: 'Other' },
];

function fileIcon(mimeType, name) {
  if (!mimeType && !name) return '📄';
  const t = (mimeType || '').toLowerCase();
  const ext = (name || '').split('.').pop().toLowerCase();
  if (t.includes('pdf') || ext === 'pdf') return '📕';
  if (t.includes('word') || ext === 'docx' || ext === 'doc') return '📘';
  if (t.includes('image') || ['jpg','jpeg','png','gif'].includes(ext)) return '🖼️';
  if (['xls','xlsx'].includes(ext)) return '📗';
  return '📄';
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── FE Upload View ─────────────────────────────────────────────
export function MopDocumentUpload({ workItemId }) {
  const [docs, setDocs]         = useState([]);
  const [docType, setDocType]   = useState('mop');
  const [uploading, setUploading] = useState(false);
  const [error, setError]       = useState(null);
  const fileRef = useRef();

  const load = () => {
    fetch(`${API}/documents?context_type=work_item&context_id=${workItemId}`, {
      headers: { Authorization: `Bearer ${token()}` },
    }).then(r => r.json()).then(d => setDocs(Array.isArray(d) ? d : []));
  };

  useEffect(() => { if (workItemId) load(); }, [workItemId]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('context_type', 'work_item');
    fd.append('context_id', String(workItemId));
    fd.append('doc_type', docType);

    try {
      const res = await fetch(`${API}/documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || `Upload failed (${res.status})`);
      else load();
    } catch {
      setError('Network error — could not reach server');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_name}"?`)) return;
    await fetch(`${API}/documents/${doc.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token()}` },
    });
    load();
  };

  const download = (doc) => {
    const a = document.createElement('a');
    a.href = `${API}/documents/${doc.id}/download`;
    a.setAttribute('download', doc.original_name);
    // Auth header can't be set on anchor — open in new tab, browser will follow with cookie/token
    // Use fetch + blob for authenticated download
    fetch(`${API}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div>
      <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>MOP Documents</div>

      {/* Uploaded docs list */}
      {docs.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {docs.map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', backgroundColor: '#1e293b', borderRadius: 7, marginBottom: 6, border: '1px solid #334155' }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>{fileIcon(doc.mime_type, doc.original_name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                  <span style={{ fontSize: 10, color: '#64748b', backgroundColor: '#0f172a', padding: '1px 6px', borderRadius: 3 }}>{DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type}</span>
                  {doc.file_size && <span style={{ fontSize: 10, color: '#475569' }}>{formatSize(doc.file_size)}</span>}
                  <span style={{ fontSize: 10, color: '#475569' }}>{doc.uploaded_by_name} · {new Date(doc.uploaded_at).toLocaleDateString()}</span>
                </div>
              </div>
              <button onClick={() => download(doc)} style={{ padding: '5px 10px', backgroundColor: '#1e3a5f', color: '#93c5fd', border: '1px solid #1d4ed8', borderRadius: 6, cursor: 'pointer', fontSize: 11, flexShrink: 0 }}>↓</button>
              <button onClick={() => remove(doc)} style={{ padding: '5px 8px', backgroundColor: 'transparent', color: '#475569', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {docs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#475569', fontSize: 13 }}>
          No documents uploaded yet. Upload the MOP and any supporting diagrams.
        </div>
      )}

      {/* Upload controls */}
      <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: 14, border: '1px solid #334155' }}>
        <div style={{ marginBottom: 10 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 5 }}>Document Type</label>
          <select value={docType} onChange={e => setDocType(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 7, color: 'white', fontSize: 13 }}>
            {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ width: '100%', padding: '9px', backgroundColor: uploading ? '#374151' : '#1e3a5f', color: uploading ? '#64748b' : '#93c5fd', border: '1px solid #1d4ed8', borderRadius: 7, cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 'bold' }}>
          {uploading ? 'Uploading…' : '📎 Attach Document'}
        </button>
        <input ref={fileRef} type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.dwg,.dxf,.csv"
          style={{ display: 'none' }}
          onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
        {error && <div style={{ color: '#f87171', fontSize: 12, marginTop: 8 }}>✗ {error}</div>}
        <div style={{ color: '#475569', fontSize: 11, marginTop: 8 }}>Accepted: PDF, Word, Excel, Images, DWG, CSV</div>
      </div>
    </div>
  );
}

// ── Tech / RPM Document Viewer ─────────────────────────────────
export function MopDocumentViewer({ workItemId }) {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (!workItemId) return;
    fetch(`${API}/documents?context_type=work_item&context_id=${workItemId}`, {
      headers: { Authorization: `Bearer ${token()}` },
    }).then(r => r.json()).then(d => setDocs(Array.isArray(d) ? d : []));
  }, [workItemId]);

  const download = (doc) => {
    fetch(`${API}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  };

  if (docs.length === 0) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>MOP Documents</div>
      {docs.map(doc => (
        <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', backgroundColor: '#1e293b', borderRadius: 7, marginBottom: 6, border: '1px solid #334155' }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>{fileIcon(doc.mime_type, doc.original_name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              <span style={{ fontSize: 10, color: '#64748b', backgroundColor: '#0f172a', padding: '1px 6px', borderRadius: 3 }}>{DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type}</span>
              {doc.file_size && <span style={{ fontSize: 10, color: '#475569' }}>{formatSize(doc.file_size)}</span>}
            </div>
          </div>
          <button onClick={() => download(doc)}
            style={{ padding: '7px 14px', backgroundColor: '#166534', color: '#4ade80', border: '1px solid #16632a', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold', flexShrink: 0 }}>
            ↓ Download
          </button>
        </div>
      ))}
    </div>
  );
}
